import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { EXPEDITION } from "@/content/expedition.js";
import { pinSize } from "@/lib/pinSize.js";

const LAYERS = {
  standard: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attr: "&copy; OpenStreetMap",
  },
  topo: {
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attr: "&copy; OpenStreetMap, SRTM | OpenTopoMap",
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attr: "Tiles &copy; Esri",
  },
};

const CAT_COLOR = {
  orignal: "#C4783A",
  traces: "#8B6B4F",
  feces: "#6B5344",
  frottage: "#A67C52",
  souille: "#5C6B4F",
  passage: "#4A6741",
  sentier: "#3D6B4F",
  zone_nourriciere: "#6B8F71",
  nourriture_naturelle: "#6B8F71",
  eau: "#4A7B8C",
  acces: "#E09A4A",
  stationnement: "#D4A574",
  chemin: "#8FA8B8",
  obstacle: "#B5523A",
  position: "#C4783A",
  secteur_interessant: "#C4783A",
  revoir: "#F0C27A",
  cache: "#1B3A2F",
  camp: "#1B3A2F",
  observation: "#C4783A",
  autre: "#7A6B5A",
};

export const TerritoryMap = forwardRef(function TerritoryMap(
  {
    center = EXPEDITION.mapCenter,
    zoom = EXPEDITION.mapCenter.zoom,
    points = [],
    onSelect,
    onBlank,
    onAddAt,
    onOpen,
    onZoomLimit,
    layer = "standard",
    selectedId,
    limit,
    tracks = [],
    testId = "territory-map",
    full = false,
    placeMode = false,
    draftPin = null,
    sheet = null,
  },
  ref,
) {
  const box = useRef(null);
  const [zoomNow, setZoomNow] = useState(zoom || 13);
  const [narrow, setNarrow] = useState(() => typeof window.matchMedia === "function" && window.matchMedia("(max-width: 899px)").matches);
  const mapRef = useRef(null);
  const LRef = useRef(null);
  const layersRef = useRef({ tiles: null, sector: null, markers: null });
  const [ready, setReady] = useState(false);

  useImperativeHandle(ref, () => ({
    invalidate() {
      mapRef.current?.invalidateSize?.();
    },
    fitSector() {
      const poly = layersRef.current.sector;
      if (poly && mapRef.current) mapRef.current.fitBounds(poly.getBounds(), { padding: [28, 28] });
    },
    flyTo(lat, lng, z = 15) {
      mapRef.current?.flyTo?.([lat, lng], z, { duration: 0.6 });
    },
    focus(lat, lng) {
      const map = mapRef.current;
      if (!map?.flyTo) return;
      const current = map.getZoom?.() || 12;
      map.flyTo([lat, lng], Math.max(current, 14), { duration: 0.45 });
    },
    fitPoints(rows) {
      if (import.meta.env.MODE === "test") return true;
      const map = mapRef.current;
      const L = LRef.current;
      const spots = (rows || []).filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
      if (!map || !L || spots.length < 2) return false;
      map.invalidateSize?.();
      map.fitBounds(L.latLngBounds(spots.map((point) => [point.latitude, point.longitude])), { padding: [56, 56], maxZoom: 13 });
      return true;
    },
    zoomBy(delta) {
      const map = mapRef.current;
      if (!map?.getZoom || !map.setZoom) return;
      map.setZoom(Math.max(8, Math.min(18, map.getZoom() + delta)));
    },
    getCenter() {
      const c = mapRef.current?.getCenter?.();
      return c ? { lat: c.lat, lng: c.lng } : null;
    },
    getZoom() {
      return mapRef.current?.getZoom?.() ?? 0;
    },
    anchor(lat, lng) {
      const map = mapRef.current;
      if (!map?.latLngToContainerPoint) return null;
      const point = map.latLngToContainerPoint([lat, lng]);
      return { x: point.x, y: point.y, width: map.getSize?.().x || 0 };
    },
  }));

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (typeof window === "undefined" || import.meta.env.MODE === "test") {
        setReady(true);
        return;
      }
      const L = await import("leaflet");
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !box.current) return;
      LRef.current = L;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      const lat = center?.lat ?? EXPEDITION.mapCenter.lat;
      const lng = center?.lng ?? EXPEDITION.mapCenter.lng;
      const map = L.map(box.current, { zoomControl: false, attributionControl: true }).setView([lat, lng], zoom);
      L.control.zoom({ position: "bottomright" }).addTo(map);
      L.control.scale({ imperial: false, position: "bottomleft" }).addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 80);
      const ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(box.current);
      map._ogRo = ro;
      setZoomNow(map.getZoom());
      setReady(true);
    }
    boot();
    return () => {
      cancelled = true;
      if (mapRef.current?._ogRo) mapRef.current._ogRo.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 899px)");
    const onChange = () => setNarrow(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const sync = () => setZoomNow(map.getZoom());
    map.on("zoomend", sync);
    sync();
    return () => map.off("zoomend", sync);
  }, [ready]);

  useEffect(() => {
    const map = mapRef.current;
    const L = LRef.current;
    if (!map || !L) return;
    if (layersRef.current.tiles) map.removeLayer(layersRef.current.tiles);
    const spec = LAYERS[layer] || LAYERS.standard;
    layersRef.current.tiles = L.tileLayer(spec.url, { maxZoom: 18, attribution: spec.attr }).addTo(map);
  }, [layer, ready]);

  useEffect(() => {
    const map = mapRef.current;
    const L = LRef.current;
    if (!map || !L) return;
    if (layersRef.current.sector) map.removeLayer(layersRef.current.sector);
    const ring = limit?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || !ring.length) return;
    const latlngs = ring.map(([lng, lat]) => [lat, lng]);
    const poly = L.polygon(latlngs, {
      color: "#E09A4A",
      weight: 4,
      fillColor: "#C4783A",
      fillOpacity: 0.18,
      className: "og-sector-glow",
    }).addTo(map);
    poly.bindTooltip("Secteur 4 — 54,55 km²");
    layersRef.current.sector = poly;
    if (full) map.fitBounds(poly.getBounds(), { padding: [24, 24] });
  }, [limit, ready, full]);

  useEffect(() => {
    const map = mapRef.current;
    const L = LRef.current;
    if (!map || !L) return;
    if (layersRef.current.markers) map.removeLayer(layersRef.current.markers);
    const group = L.layerGroup();
    const numbered = points.filter((point) => point.number);
    const plain = points.filter((point) => !point.number);
    const packs = groupNearby(numbered, map, zoomNow, narrow);
    const clustered = clusterPoints(plain, full && plain.length > 12);
    packs.forEach((entry) => {
      if (entry.kind === "pack") {
        const size = pinSize(zoomNow, narrow, false);
        const marker = L.marker([entry.lat, entry.lng], {
          icon: L.divIcon({
            className: "og-spotpin",
            html: `<span style="font-size:${Math.round(size * 0.38)}px">${entry.points.length}</span>`,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
          }),
          zIndexOffset: 400,
          keyboard: false,
        });
        marker.bindTooltip(entry.points.map((point) => `#${point.number}`).join("  "));
        marker.on("click", (ev) => {
          L.DomEvent.stopPropagation(ev);
          if (placeMode) {
            onAddAt?.({ latitude: entry.points[0].latitude, longitude: entry.points[0].longitude });
            return;
          }
          map.flyTo([entry.lat, entry.lng], Math.min((map.getZoom() || 13) + 2, 16), { duration: 0.35 });
        });
        marker.addTo(group);
        return;
      }
      const point = entry.point;
      const active = selectedId && point.id === selectedId;
      const size = pinSize(zoomNow, narrow, active);
      const marker = L.marker([active ? point.latitude : (entry.lat ?? point.latitude), active ? point.longitude : (entry.lng ?? point.longitude)], {
        icon: L.divIcon({
          className: `og-spotpin${point.pending ? " is-new" : ""}${point.fresh || point.tonight ? " is-today" : ""}${point.tonight ? " is-tonight" : ""}${point.pending && !point.fresh && !point.tonight ? " is-wait" : ""}${active ? " is-on" : ""}`,
          html: `<span style="font-size:${Math.round(size * 0.42)}px">${point.number}</span>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        }),
        zIndexOffset: active ? 800 : 0,
        keyboard: false,
      });
      marker.bindTooltip(`#${point.number} ${point.name || ""}`);
      marker.on("click", (ev) => {
        L.DomEvent.stopPropagation(ev);
        if (placeMode) {
          onAddAt?.({ latitude: point.latitude, longitude: point.longitude });
          return;
        }
        onSelect?.(point);
      });
      marker.addTo(group);
    });
    clustered.forEach((entry) => {
      if (entry.kind === "cluster") {
        const marker = L.circleMarker([entry.lat, entry.lng], {
          radius: 14,
          color: "#F3E6C8",
          fillColor: "#1B3A2F",
          fillOpacity: 0.95,
          weight: 2,
        });
        marker.bindTooltip(entry.photoCluster ? `${entry.count} photos` : `${entry.count} points`);
        marker.on("click", (ev) => {
          L.DomEvent.stopPropagation(ev);
          if (placeMode) {
            onAddAt?.({ latitude: entry.lat, longitude: entry.lng });
            return;
          }
          map.flyTo([entry.lat, entry.lng], Math.min((map.getZoom() || 12) + 2, 16), { duration: 0.4 });
        });
        marker.addTo(group);
        return;
      }
      const point = entry.point;
      const active = selectedId && point.id === selectedId;
      const color = point.marker === "photo" ? "#f4c57a" : CAT_COLOR[point.category] || CAT_COLOR.autre;
      const marker = L.circleMarker([point.latitude, point.longitude], {
          radius: active ? 12 : point.marker === "photo" ? 9 : point.revisit ? 10 : 8,
          color: point.marker === "photo" ? "#fff8ee" : active ? "#F0C27A" : "#07120e",
          fillColor: color,
          fillOpacity: 0.95,
          weight: active ? 3 : 2,
          className: active ? "og-pulse" : "",
        });
        marker.bindTooltip(
          point.marker === "photo"
            ? `${point.count ? `${point.count} photos` : "Photo"} · ${point.date_heure || point.name || ""}`
            : point.number ? `#${point.number} ${point.name || ""}` : (point.comment || point.name || point.category),
        );
      marker.on("click", (ev) => {
        L.DomEvent.stopPropagation(ev);
        if (placeMode) {
          onAddAt?.({ latitude: ev.latlng.lat, longitude: ev.latlng.lng });
          return;
        }
        onSelect?.(point);
      });
      marker.addTo(group);
    });
    (tracks || []).forEach((tr) => {
      const latlngs = (tr.coords || []).map((c) => [c[0], c[1]]);
      if (latlngs.length < 2) return;
      const style = { color: tr.kind === "polygon" ? "#6B8F71" : "#C4783A", weight: 3, opacity: 0.85, fillOpacity: tr.kind === "polygon" ? 0.12 : 0 };
      const layer = tr.kind === "polygon" ? L.polygon(latlngs, style) : L.polyline(latlngs, style);
      layer.bindTooltip(tr.name || tr.kind);
      layer.addTo(group);
    });
    group.addTo(map);
    layersRef.current.markers = group;
  }, [points, tracks, selectedId, onSelect, onAddAt, placeMode, ready, full, zoomNow, narrow]);

  useEffect(() => {
    const map = mapRef.current;
    const L = LRef.current;
    if (!map || !L) return undefined;
    if (layersRef.current.sheet) {
      layersRef.current.sheet.remove();
      layersRef.current.sheet = null;
    }
    if (sheet?.tiles || !sheet?.image) return undefined;
    const placed = placeSheet(map, sheet);
    layersRef.current.sheet = placed;
    return () => {
      placed.remove();
      if (layersRef.current.sheet === placed) layersRef.current.sheet = null;
    };
  }, [sheet, ready]);

  useEffect(() => {
    const map = mapRef.current;
    const L = LRef.current;
    if (!map || !L || !sheet?.tiles) return undefined;
    const spec = sheet.tiles;
    const layer = L.tileLayer(spec.url, {
      minZoom: spec.minZoom,
      maxNativeZoom: spec.maxNativeZoom,
      maxZoom: 19,
      bounds: spec.bounds,
      opacity: 1,
    }).addTo(map);
    const report = () => onZoomLimit?.(map.getZoom() > spec.maxNativeZoom);
    map.on("zoomend", report);
    report();
    return () => {
      map.off("zoomend", report);
      map.removeLayer(layer);
    };
  }, [sheet, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handler = (ev) => {
      if (placeMode) {
        onAddAt?.({ latitude: ev.latlng.lat, longitude: ev.latlng.lng });
        return;
      }
      onBlank?.();
    };
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [onAddAt, onBlank, placeMode, ready]);

  useEffect(() => {
    const map = mapRef.current;
    const L = LRef.current;
    if (!map || !L) return undefined;
    if (layersRef.current.draft) {
      map.removeLayer(layersRef.current.draft);
      layersRef.current.draft = null;
    }
    if (!draftPin || !Number.isFinite(Number(draftPin.latitude)) || !Number.isFinite(Number(draftPin.longitude))) return undefined;
    const draftSize = pinSize(zoomNow, narrow, true);
    const marker = L.marker([Number(draftPin.latitude), Number(draftPin.longitude)], {
      icon: L.divIcon({
        className: "og-spotpin is-on is-today",
        html: `<span style="font-size:${Math.round(draftSize * 0.42)}px">+</span>`,
        iconSize: [draftSize, draftSize],
        iconAnchor: [draftSize / 2, draftSize / 2],
      }),
      zIndexOffset: 900,
    });
    marker.bindTooltip("Nouveau point");
    marker.addTo(map);
    layersRef.current.draft = marker;
    return undefined;
  }, [draftPin, ready, zoomNow, narrow]);

  useEffect(() => {
    if (!mapRef.current || !center?.lat) return;
    if (full) return;
    mapRef.current.setView([center.lat, center.lng], center.zoom || zoom);
  }, [center?.lat, center?.lng, center?.zoom, zoom, full]);

  return (
    <div
      className={`og-map-shell${full ? " og-map-shell--full" : ""}${placeMode ? " is-placing" : ""}`}
      onClick={() => onOpen?.()}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (onOpen && (e.key === "Enter" || e.key === " ")) onOpen();
      }}
    >
      <div
        ref={box}
        className={full ? "og-map og-map--full" : "og-map"}
        data-testid={testId}
        data-ready={ready || import.meta.env.MODE === "test" ? "1" : "0"}
        data-placing={placeMode ? "1" : "0"}
        data-draft={draftPin ? "1" : "0"}
        onClick={(event) => {
          if (import.meta.env.MODE === "test" && placeMode) {
            event.stopPropagation();
            onAddAt?.({ latitude: 47.3, longitude: -72.48 });
            return;
          }
          if (event.target === event.currentTarget) onBlank?.();
        }}
      >
        {import.meta.env.MODE === "test"
          ? points.filter((point) => point.number).map((point) => (
            <button
              key={point.id}
              type="button"
              data-testid={`map-pin-${point.id}`}
              data-number={point.number}
              className={selectedId === point.id ? "is-on" : ""}
              onClick={(event) => {
                event.stopPropagation();
                onSelect?.(point);
              }}
            >
              {point.number}
            </button>
          ))
          : null}
      </div>
    </div>
  );
});

function placeSheet(map, sheet) {
  const pane = map.getPane("overlayPane");
  const image = document.createElement("img");
  image.className = "og-sepaq-sheet";
  image.alt = "";
  image.src = sheet.image;
  pane.appendChild(image);
  function place() {
    if (!image.naturalWidth || !map.latLngToLayerPoint) return;
    const topLeft = map.latLngToLayerPoint(sheet.topLeft);
    const topRight = map.latLngToLayerPoint(sheet.topRight);
    const bottomLeft = map.latLngToLayerPoint(sheet.bottomLeft);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    image.style.transform = `matrix(${(topRight.x - topLeft.x) / width}, ${(topRight.y - topLeft.y) / width}, ${(bottomLeft.x - topLeft.x) / height}, ${(bottomLeft.y - topLeft.y) / height}, ${topLeft.x}, ${topLeft.y})`;
  }
  image.onload = place;
  map.on("move zoom viewreset resize", place);
  place();
  return {
    remove() {
      map.off("move zoom viewreset resize", place);
      image.remove();
    },
  };
}

function groupNearby(points, map, zoom, phone) {
  if (!map?.latLngToContainerPoint || zoom >= 15) {
    return points.map((point) => ({ kind: "point", point }));
  }
  const limit = pinSize(zoom, phone, false) * 0.85;
  const used = new Set();
  const out = [];
  points.forEach((point, index) => {
    if (used.has(index) || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return;
    const origin = map.latLngToContainerPoint([point.latitude, point.longitude]);
    const bunch = [point];
    used.add(index);
    points.forEach((other, otherIndex) => {
      if (used.has(otherIndex) || !Number.isFinite(other.latitude)) return;
      const there = map.latLngToContainerPoint([other.latitude, other.longitude]);
      if (Math.hypot(origin.x - there.x, origin.y - there.y) < limit) {
        bunch.push(other);
        used.add(otherIndex);
      }
    });
    if (bunch.length === 1 || !map.containerPointToLatLng) {
      out.push({ kind: "point", point });
    } else {
      const size = pinSize(zoom, phone, false);
      bunch.forEach((item, bunchIndex) => {
        if (bunchIndex === 0) {
          out.push({ kind: "point", point: item });
          return;
        }
        const origin = map.latLngToContainerPoint([item.latitude, item.longitude]);
        const ring = Math.ceil(bunchIndex / 6);
        const angle = ((bunchIndex - 1) * 2 * Math.PI) / Math.min(bunch.length - 1, 6);
        const shifted = map.containerPointToLatLng([
          origin.x + Math.cos(angle) * size * 0.9 * ring,
          origin.y + Math.sin(angle) * size * 0.9 * ring,
        ]);
        out.push({ kind: "point", point: item, lat: shifted.lat, lng: shifted.lng });
      });
    }
  });
  return out;
}

function clusterPoints(points, enabled) {
  if (!enabled) return points.map((p) => ({ kind: "point", point: p }));
  const buckets = new Map();
  for (const p of points) {
    const key = `${(p.latitude || 0).toFixed(3)}:${(p.longitude || 0).toFixed(3)}`;
    const list = buckets.get(key) || [];
    list.push(p);
    buckets.set(key, list);
  }
  const out = [];
  for (const list of buckets.values()) {
        if (list.length === 1) out.push({ kind: "point", point: list[0] });
        else {
          const photos = list.filter((p) => p.marker === "photo").length;
          out.push({
            kind: "cluster",
            lat: list.reduce((s, p) => s + p.latitude, 0) / list.length,
            lng: list.reduce((s, p) => s + p.longitude, 0) / list.length,
            count: list.length,
            photoCluster: photos === list.length,
          });
        }
  }
  return out;
}

export const MAP_LAYERS = Object.keys(LAYERS);
export const POINT_COLORS = CAT_COLOR;
